import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { getAuthToken, setAuthToken } from "../utils/access";
import { logout } from "./Auth/logout";

const WebSocketContext = createContext();

export const useWebSocketContext = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error("useWebSocketContext must be used inside WebSocketProvider");
  return ctx;
};

const PING_INTERVAL = 20000;
// if the server has shown it answers pings, treat this much silence as a dead connection
const STALE_TIMEOUT = PING_INTERVAL * 3;
const CONNECT_TIMEOUT = 10000;
const TOKEN_REFRESH_INTERVAL = 15 * 60 * 1000;

const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const handlersRef = useRef({});
  const socketRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const messageQueueRef = useRef([]);
  const shouldReconnectRef = useRef(true);
  const lastMessageAtRef = useRef(0);
  const serverAnswersPingRef = useRef(false);
  const inflightRef = useRef({});
  const sendNowRef = useRef(null);

  // const url = process.env.REACT_APP_WS_URL || "ws://192.168.31.45:8089/ws";
  // const url = process.env.REACT_APP_WS_URL || "wss://api-backend.tetramotors.com/ws";
  const url = process.env.REACT_APP_WS_URL || "wss://ocpp.tetramotors.com/ws";
  // const url = process.env.REACT_APP_WS_URL || "ws://35.244.32.164/ws";
  // const url =  "wss://api-backend-prod.tetramotors.com/ws";



  // live connection check; safe to call from stale closures (unlike the isConnected state value)
  const isOpen = useCallback(() => socketRef.current?.readyState === WebSocket.OPEN, []);

  // resolves once the current socket is open (or rejects after CONNECT_TIMEOUT)
  const waitForOpen = useCallback(() => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const start = Date.now();
      const interval = setInterval(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          clearInterval(interval);
          resolve();
        } else if (Date.now() - start > CONNECT_TIMEOUT) {
          clearInterval(interval);
          reject(new Error("WebSocket connection timeout"));
        }
      }, 100);
    });
  }, []);

  // --- connect with exponential backoff ---
  const connect = useCallback(() => {
    shouldReconnectRef.current = true;

    // already open or opening: never create a second socket
    const existing = socketRef.current;
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
      return waitForOpen();
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const socket = new WebSocket(url);
    socketRef.current = socket;

    socket.onopen = () => {
      if (socketRef.current !== socket) return;
      console.log("[WS] Connected");
      setIsConnected(true);
      setError(null);
      reconnectAttemptRef.current = 0;
      lastMessageAtRef.current = Date.now();

      // flush queued messages
      messageQueueRef.current.forEach((msg) => socket.send(JSON.stringify(msg)));
      messageQueueRef.current = [];
    };

    socket.onclose = (event) => {
      // ignore close events from sockets that have already been replaced
      if (socketRef.current !== socket) return;
      socketRef.current = null;
      setIsConnected(false);

      if (!shouldReconnectRef.current) {
        console.log("[WS] Closed");
        return;
      }

      setError("Connection closed, retrying...");
      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      const timeout = Math.min(1000 * 2 ** (attempt - 1), 30000);
      console.log(`[WS] Closed (code ${event.code}), reconnecting in ${timeout / 1000}s`);
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connect();
      }, timeout);
    };

    socket.onerror = (err) => {
      if (socketRef.current !== socket) return;
      console.error("[WS] Error", err);
      setError("Connection error");
    };

    socket.onmessage = (event) => {
      if (socketRef.current !== socket) return;
      lastMessageAtRef.current = Date.now();
      try {
        const data = JSON.parse(event.data);
        const type = data.type;
        if (type === "cms_ping_response" || type === "cms_pong" || type === "pong") {
          serverAnswersPingRef.current = true;
        }
        // the server refused the login token (expired or invalid): back to the login page
        if (data.code === "unauthorized" && getAuthToken() && window.location.pathname !== "/login") {
          console.warn("[WS] Session expired, logging out");
          logout();
          return;
        }
        if (handlersRef.current[type]) {
          handlersRef.current[type].forEach((h) => h(data));
        }
      } catch (e) {
        console.error("[WS] Invalid message:", e);
      }
    };

    return waitForOpen();
  }, [url, waitForOpen]);

  // force-drop the current socket and reconnect right away (used for dead/half-open connections)
  const reconnectNow = useCallback(() => {
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket) {
      socket.onopen = socket.onclose = socket.onerror = socket.onmessage = null;
      try { socket.close(); } catch (e) { /* ignore */ }
    }
    setIsConnected(false);
    reconnectAttemptRef.current = 0;
    connect().catch(() => {});
  }, [connect]);

  // --- disconnect ---
  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket) {
      socket.onopen = socket.onclose = socket.onerror = socket.onmessage = null;
      socket.close(1000, "Client disconnect");
    }
    setIsConnected(false);
  }, []);

  // --- register handler ---
  const registerHandler = useCallback((type, handler) => {
    if (!handlersRef.current[type]) handlersRef.current[type] = [];
    handlersRef.current[type].push(handler);
    return () => {
      if (!handlersRef.current[type]) return;
      handlersRef.current[type] = handlersRef.current[type].filter((h) => h !== handler);
      if (handlersRef.current[type].length === 0) delete handlersRef.current[type];
    };
  }, []);

  // --- send (queue if not connected) ---
  // Responses carry no request id and are matched only by `${type}_response`, so two
  // requests of the same type in flight at once would both receive the first reply.
  // Requests are therefore sent one at a time per type; an identical request that is
  // already pending shares its result instead of being sent again.
  // options.timeoutMs: how long to wait for the reply (default 10s)
  const send = useCallback(
    (type, payload = {}, options = {}) => {
      const key = JSON.stringify(payload);
      const lane = inflightRef.current[type] || (inflightRef.current[type] = { tail: Promise.resolve(), pending: new Map() });
      if (lane.pending.has(key)) return lane.pending.get(key);

      const request = lane.tail.catch(() => {}).then(() => sendNowRef.current(type, payload, options));
      lane.pending.set(key, request);
      lane.tail = request;
      request
        .finally(() => {
          if (lane.pending.get(key) === request) lane.pending.delete(key);
          if (lane.tail === request && inflightRef.current[type] === lane) delete inflightRef.current[type];
        })
        .catch(() => {});
      return request;
    },
    []
  );

  const sendNow = useCallback(
    (type, payload = {}, { timeoutMs = 10000 } = {}) => {
      const message = { ...payload, type };
      // the server reads the user's group from this token (it no longer trusts group_id alone)
      const token = getAuthToken();
      if (token && message.token === undefined) message.token = token;
      const responseType = `${type}_response`;

      // register before sending so a fast response can't be missed
      const response = new Promise((resolve, reject) => {
        const handler = (msg) => {
          unregister();
          clearTimeout(timeoutId);
          resolve(msg);
        };

        const unregister = registerHandler(responseType, handler);

        const timeoutId = setTimeout(() => {
          unregister();
          reject(new Error(`Timeout waiting for ${responseType}`));
        }, timeoutMs);
      });

      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(message));
      } else {
        messageQueueRef.current.push(message);
        connect().catch(() => {});
      }

      return response;
    },
    [registerHandler, connect]
  );
  sendNowRef.current = sendNow;

  // --- init connection ---
  useEffect(() => {
    connect().catch(() => {});
    return () => disconnect();
  }, [connect, disconnect]);

  // --- reconnect immediately when the network comes back or the tab becomes visible ---
  useEffect(() => {
    const revive = () => {
      if (document.visibilityState === "hidden") return;
      const socket = socketRef.current;
      if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
        reconnectNow();
      } else if (
        socket.readyState === WebSocket.OPEN &&
        serverAnswersPingRef.current &&
        Date.now() - lastMessageAtRef.current > STALE_TIMEOUT
      ) {
        reconnectNow();
      }
    };

    window.addEventListener("online", revive);
    document.addEventListener("visibilitychange", revive);
    return () => {
      window.removeEventListener("online", revive);
      document.removeEventListener("visibilitychange", revive);
    };
  }, [reconnectNow]);

  // --- keepalive ping + dead connection detection ---
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      const socket = socketRef.current;
      if (socket?.readyState !== WebSocket.OPEN) return;

      if (serverAnswersPingRef.current && Date.now() - lastMessageAtRef.current > STALE_TIMEOUT) {
        console.warn("[WS] No response from server, reconnecting");
        reconnectNow();
        return;
      }

      socket.send(JSON.stringify({ type: "cms_ping", payload: { data: "ping/pong" } }));
    }, PING_INTERVAL);

    return () => clearInterval(interval);
  }, [isConnected, reconnectNow]);

  // --- keep the login token fresh (it expires 25 min after it was issued) ---
  useEffect(() => {
    if (!isConnected) return;

    const refresh = async () => {
      if (!getAuthToken()) return;
      try {
        const response = await send("cms_refresh_token", {});
        if (response?.success && response.token) setAuthToken(response.token);
      } catch (e) {
        // older servers don't support renewal; requests keep using the current token
      }
    };

    refresh();
    const interval = setInterval(refresh, TOKEN_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [isConnected, send]);

  return (
    <WebSocketContext.Provider value={{ isConnected, isOpen, error, send, registerHandler, connect }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
